import { createUserWithEmailAndPassword, updatePassword, signInWithEmailAndPassword, getAuth } from 'firebase/auth';
import { updateDoc, doc, query, collection, where, getDocs } from 'firebase/firestore';
import { auth, db, app } from './firebase';
import { initializeApp } from 'firebase/app';

// Generate a random password
export const generateWholesalePassword = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
  let password = '';
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

// Generate a unique account number
export const generateAccountNumber = () => {
  const timestamp = Date.now().toString();
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `WS${timestamp.slice(-6)}${random}`;
};

// Create wholesale login credentials when a customer is approved
export const createWholesaleCredentials = async (customerId, customerData, accountType = 'approved') => {
  try {
    // Use the email from the wholesale application form (purchaseEmail)
    const customerEmail = customerData.purchaseEmail || customerData.email;
    
    if (!customerEmail) {
      throw new Error('No email found in customer data');
    }

    // Safety check: Don't create wholesale credentials for admin emails
    const adminEmails = ['admin@grey.com', 'admin@greycoffee.com']; // Add your admin emails here
    if (adminEmails.includes(customerEmail.toLowerCase())) {
      throw new Error('Cannot create wholesale credentials for admin email addresses');
    }
    
    // Generate unique credentials
    const wholesalePassword = generateWholesalePassword();
    const accountNumber = generateAccountNumber();
    
    console.log('Creating wholesale credentials for customer:', customerId);

    // Update the customer document with wholesale credentials
    // No Firebase Auth user creation to avoid interfering with admin session
    const customerRef = doc(db, 'wholesaleCustomers', customerId);
    const updateData = {
      wholesaleEmail: customerEmail, // Store the customer's actual email for login
      wholesalePassword: wholesalePassword, // Store for admin to share
      accountNumber: accountNumber,
      approvedAt: new Date(),
      // Create a virtual UID for internal tracking
      wholesaleUid: `ws_${customerId}_${Date.now()}`
    };
    
    // Always set the status to ensure it's correct
    updateData.status = accountType;
    
    if (accountType === 'demo') {
      updateData.demoStartDate = new Date();
      updateData.demoEndDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000); // 30 days demo
      updateData.demoLimitations = {
        readOnly: true,
        canViewProducts: true,
        canViewPricing: true,
        canOrder: false,
        accessLevel: 'demo'
      };
    } else if (accountType === 'trial') {
      updateData.trialStartDate = new Date();
      updateData.trialEndDate = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000); // 14 days trial
      updateData.trialLimitations = {
        maxOrderValue: 1000, // QAR 1000 max order
        maxMonthlyOrders: 3,
        accessLevel: 'limited'
      };
    }
    
    await updateDoc(customerRef, updateData);

    console.log('Customer document updated successfully - no Auth user created');

    return {
      email: customerEmail, // Return customer's actual email for login
      accountNumber: accountNumber,
      password: wholesalePassword,
      uid: `ws_${customerId}_${Date.now()}`
    };
  } catch (error) {
    console.error('Error creating wholesale credentials:', error);
    
    // Handle specific Firebase errors
    if (error.code === 'auth/email-already-in-use') {
      // Try with a different unique identifier
      const timestamp = Date.now();
      const randomSuffix = Math.random().toString(36).substring(7);
      const customerEmail = customerData.purchaseEmail || customerData.email;
      const emailParts = customerEmail.split('@');
      const alternativeEmail = `ws.${timestamp}.${randomSuffix}.${emailParts[0]}@${emailParts[1]}`;
      
      try {
        const userCredential = await createUserWithEmailAndPassword(auth, alternativeEmail, generateWholesalePassword());
        const user = userCredential.user;
        const accountNumber = generateAccountNumber();
        const wholesalePassword = generateWholesalePassword();

        const customerRef = doc(db, 'wholesaleCustomers', customerId);
        await updateDoc(customerRef, {
          status: 'approved',
          wholesaleEmail: customerEmail,
          wholesaleAuthEmail: alternativeEmail,
          wholesalePassword: wholesalePassword,
          accountNumber: accountNumber,
          wholesaleUid: user.uid,
          approvedAt: new Date()
        });

        return {
          email: customerEmail,
          accountNumber: accountNumber,
          password: wholesalePassword,
          uid: user.uid
        };
      } catch (retryError) {
        throw new Error(`Failed to create wholesale account after retry: ${retryError.message}`);
      }
    }
    
    throw error;
  }
};

// Create demo account for sales presentations (read-only)
export const createDemoAccount = async (customerId, customerData) => {
  return await createWholesaleCredentials(customerId, customerData, 'demo');
};

// Create trial account for potential customers
export const createTrialAccount = async (customerId, customerData) => {
  return await createWholesaleCredentials(customerId, customerData, 'trial');
};

// Convert trial account to full approval
export const convertTrialToApproved = async (customerId) => {
  try {
    const customerRef = doc(db, 'wholesaleCustomers', customerId);
    await updateDoc(customerRef, {
      status: 'approved',
      convertedToFullAt: new Date(),
      trialLimitations: null // Remove trial limitations
    });
  } catch (error) {
    console.error('Error converting trial to approved:', error);
    throw error;
  }
};

// Update customer status without creating credentials (for rejections, etc.)
export const updateCustomerStatus = async (customerId, status) => {
  try {
    const customerRef = doc(db, 'wholesaleCustomers', customerId);
    await updateDoc(customerRef, { 
      status,
      updatedAt: new Date()
    });
  } catch (error) {
    console.error('Error updating customer status:', error);
    throw error;
  }
};

// Change wholesale password
export const changeWholesalePassword = async (customerId, newPassword) => {
  try {
    // Update Firebase Auth password
    if (auth.currentUser) {
      await updatePassword(auth.currentUser, newPassword);
    }
    
    // Update the stored password in the customer document (temporary storage)
    const customerRef = doc(db, 'wholesaleCustomers', customerId);
    await updateDoc(customerRef, {
      wholesalePassword: newPassword,
      passwordChangedAt: new Date()
    });
  } catch (error) {
    console.error('Error changing wholesale password:', error);
    throw error;
  }
};